import { useState, useEffect, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentUser } from '../../store/user/user.selector';
import { fetchUserFromFirestore } from '../../utils/firebase/firebase.utils';
import { setUser } from '../../store/user/user.action';
import { collection, getDocs, query } from 'firebase/firestore';
import { db } from '../../utils/firebase/firebase.utils';

// styles
import {
  TokenPageContainer,
  TokenTiersContainer,
  BalanceContainer,
  TokenBalanceDisplay,
  PromoCodeInput,
  PromoCodeRow,
  LoadingSpinner,
  SuccessMessage,
  ErrorMessage,
  TokenActivityContainer
} from './TokenPage.styles';

// components
import tokenIcon from '../../assets/system/icons/pullvault-token.png';
import InputBar from '../../components/input-bar/InputBar';
import PaymentForm from './components/payment-form/PaymentForm';
import PrimaryBtn from '../../components/btns/primary-btn/PrimaryBtn';
import TokenTierCard from './components/token-tier-card/TokenTierCard';
import ActivityLogList from '../../components/activity-log-list/ActivityLogList';

// Initialize Stripe (you'll need to add your publishable key to environment variables)
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

export default function TokenPage() {
  const [selectedTier, setSelectedTier] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [isPromoCodeValid, setIsPromoCodeValid] = useState(false);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [promoCodeError, setPromoCodeError] = useState('');
  const [promoCodeRedeemed, setPromoCodeRedeemed] = useState(false);
  const [promoRedeemSuccessMessage, setPromoRedeemSuccessMessage] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [tokenActivities, setTokenActivities] = useState([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);

  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const functions = getFunctions();

  // Fetch token-related activities
  const fetchTokenActivities = useCallback(async () => {
    if (!currentUser?.uid) return;

    setIsLoadingActivities(true);
    try {
      const userActivitiesRef = collection(db, 'users', currentUser.uid, 'activityLog');
      const q = query(userActivitiesRef);
      const querySnapshot = await getDocs(q);
      const allActivities = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      const activities = allActivities
        .filter(activity => ['tokenPurchase', 'checklistUnlocked', 'promoCodeRedeemed', 'welcomeTokensGranted'].includes(activity.actionType))
        .sort((a, b) => {
          const timestampA = a.timestamp || a.createdAt || 0;
          const timestampB = b.timestamp || b.createdAt || 0;
          return timestampB - timestampA;
        });
      setTokenActivities(activities);
    } catch (error) {
      // Error fetching activities - silent fail, user still sees empty list
    } finally {
      setIsLoadingActivities(false);
    }
  }, [currentUser?.uid]);

  // Fetch token activities on component mount and when user changes
  useEffect(() => {
    fetchTokenActivities();
  }, [fetchTokenActivities]);

  const tokenTiers = [
    {
      id: 'tier-1',
      name: 'Rookie',
      tokens: 1,
      price: 5,
      description: '1 Token'
    },
    {
      id: 'tier-2',
      name: 'Pro',
      tokens: 5,
      price: 20,
      description: '5 Tokens'
    },
    {
      id: 'tier-3',
      name: 'Hall of Fame',
      tokens: 10,
      price: 30,
      description: '10 Tokens'
    }
  ];

  // Validate and automatically redeem promo code
  const validatePromoCode = async (code) => {
    if (!code.trim()) return;

    setIsValidatingPromo(true);
    setPromoCodeError('');

    try {
      const validatePromoCodeFunction = httpsCallable(functions, 'validatePromoCode');
      const result = await validatePromoCodeFunction({ code: code.trim() });

      if (result.data.isValid) {
        setIsPromoCodeValid(true);
        setPromoCodeError('');
        await handleRedeemPromoCode(code.trim());
      } else {
        setIsPromoCodeValid(false);
        setPromoCodeError(result.data.message);
      }
    } catch (error) {
      setIsPromoCodeValid(false);
      setPromoCodeError('Failed to validate promo code. Please try again.');
    } finally {
      setIsValidatingPromo(false);
    }
  };

  // Handle promo code input
  const handlePromoCodeChange = (value) => {
    // Remove spaces, filter to only alphanumeric characters, and convert to uppercase
    const sanitized = value
      .replace(/\s/g, '') // Remove all spaces
      .replace(/[^a-zA-Z0-9]/g, '') // Remove non-alphanumeric characters
      .toUpperCase(); // Convert to uppercase
    setPromoCode(sanitized);
    // Clear errors and success messages when user types
    setPromoCodeError('');
    setPromoCodeRedeemed(false);
    setPromoRedeemSuccessMessage('');
    // Don't auto-validate on every keystroke, only when ENTER button is clicked
  };

  // Handle ENTER button click for promo code validation
  const handleEnterPromoCode = () => {
    if (promoCode.trim()) validatePromoCode(promoCode);
  };

  // Redeem promo code for free token
  const handleRedeemPromoCode = async (codeToRedeem = null) => {
    const code = codeToRedeem || promoCode.trim();

    if (!code) {
      setPromoCodeError('Please enter and validate a promo code first');
      return;
    }

    if (!currentUser || !currentUser.uid) {
      setPromoCodeError('User not logged in. Please log in again.');
      return;
    }

    setIsProcessingPayment(true);
    setPromoCodeError('');
    setPaymentError('');
    setPaymentSuccess(false);

    try {
      const redeemPromoCodeFunction = httpsCallable(functions, 'redeemPromoCode');
      const result = await redeemPromoCodeFunction({ code: code });

      if (result.data.success) {
        setPromoCodeRedeemed(true);
        setPromoRedeemSuccessMessage(
          result.data.message ||
            'Promo code redeemed successfully! Your tokens have been added to your account.'
        );
        setPromoCode('');
        setIsPromoCodeValid(false);
        setPromoCodeError('');

        // Update user state with new token balance from the function response
        // This ensures immediate UI update without waiting for Firestore consistency
        if (currentUser && result.data.newTokenBalance !== undefined) {
          const updatedUser = {
            ...currentUser,
            tokens: result.data.newTokenBalance
          };
          dispatch(setUser(updatedUser));
        } else {
          // Fallback: fetch from Firestore if response doesn't have balance
          const updatedUser = await fetchUserFromFirestore(currentUser.uid);
          if (updatedUser) {
            dispatch(setUser(updatedUser));
          }
        }

        // Refresh token activities to show the new redemption
        fetchTokenActivities();
      } else {
        throw new Error(result.data.message || 'Failed to redeem promo code');
      }
    } catch (error) {
      setPromoCodeError(error.message || 'Failed to redeem promo code. Please try again.');
      setIsPromoCodeValid(false);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Process payment
  const handlePayment = async (paymentMethod) => {
    if (!selectedTier) {
      setPaymentError('Please select a token tier');
      return;
    }

    if (!currentUser || !currentUser.uid) {
      setPaymentError('User not logged in. Please log in again.');
      setIsProcessingPayment(false);
      return;
    }

    setIsProcessingPayment(true);
    setPaymentError('');
    setPaymentSuccess(false);
    // Clear promo code notifications when purchase is initiated
    setPromoCodeError('');
    setPromoCodeRedeemed(false);
    setPromoRedeemSuccessMessage('');

    try {
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      // Create payment intent via Firebase Function
      const createPaymentIntentFunction = httpsCallable(functions, 'createPaymentIntent');
      const paymentIntentResult = await createPaymentIntentFunction({
        amount: selectedTier.price * 100, // Convert to cents
        currency: 'usd',
        metadata: {
          tokens: selectedTier.tokens.toString(),
          userId: currentUser?.uid,
          tierId: selectedTier.id
        }
      });

      if (paymentIntentResult.data.error) {
        throw new Error(paymentIntentResult.data.error);
      }

      if (!paymentIntentResult.data.clientSecret) {
        throw new Error('No client secret received from payment intent');
      }

      // Confirm payment with Stripe
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        paymentIntentResult.data.clientSecret,
        {
          payment_method: paymentMethod.id
        }
      );

      if (error) throw new Error(error.message);

      // Process token purchase securely on server-side
      const processTokenPurchaseFunction = httpsCallable(functions, 'processTokenPurchase');
      const purchaseResult = await processTokenPurchaseFunction({
        paymentIntentId: paymentIntent.id,
        tier: selectedTier,
        promoBonus: isPromoCodeValid ? 1 : 0
      });

      if (!purchaseResult.data.success) {
        throw new Error('Failed to process token purchase');
      }

      // Update user state with new token balance from the function response
      // This ensures immediate UI update without waiting for Firestore consistency
      if (currentUser && purchaseResult.data.newTokenBalance !== undefined) {
        const updatedUser = {
          ...currentUser,
          tokens: purchaseResult.data.newTokenBalance
        };
        dispatch(setUser(updatedUser));
      } else {
        // Fallback: fetch from Firestore if response doesn't have balance
        const updatedUser = await fetchUserFromFirestore(currentUser.uid);
        if (updatedUser) {
          dispatch(setUser(updatedUser));
        }
      }

      setPaymentSuccess(true);
      setPaymentError('');
      setSuccessMessage('Payment successful! Your tokens have been added to your account.');
      setSelectedTier(null);
      setPromoCode('');
      setIsPromoCodeValid(false);

      // Refresh token activities to show the new purchase
      fetchTokenActivities();

    } catch (error) {
      setPaymentError(error.message || 'Payment failed. Please try again.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  return (
    <Elements stripe={stripePromise}>
      <TokenPageContainer>
          <div className="heading-container">
            <BalanceContainer>
              <h1>TOKENS</h1>
              <TokenBalanceDisplay>
                <img src={tokenIcon} alt="Token" />
                <span>{currentUser?.tokens ?? 0}</span>
              </TokenBalanceDisplay>
            </BalanceContainer>
          </div>

          <PromoCodeInput>
            <PromoCodeRow>
              <InputBar
                type="text"
                id="promoCode"
                placeholder="Enter code..."
                label="Promo Code"
                value={promoCode}
                updateValue={handlePromoCodeChange}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleEnterPromoCode();
                  }
                }}
                disabled={isValidatingPromo || isProcessingPayment}
              />
              <PrimaryBtn
                title="ENTER CODE"
                onClick={handleEnterPromoCode}
                disabled={isValidatingPromo || isProcessingPayment || !promoCode.trim()}
                isLoading={isValidatingPromo || isProcessingPayment}
                style={{ width: '80px', height: '56px' }}
              />
            </PromoCodeRow>
            {promoCodeError && (
              <ErrorMessage>{promoCodeError}</ErrorMessage>
            )}
            {promoCodeRedeemed && !promoCodeError && promoRedeemSuccessMessage && (
              <SuccessMessage>{promoRedeemSuccessMessage}</SuccessMessage>
            )}
          </PromoCodeInput>

        <TokenTiersContainer>
        {tokenTiers.map((tier) => (
          <TokenTierCard
            key={tier.id}
            title={tier.name}
            price={tier.price}
            description={tier.description}
            isSelected={selectedTier?.id === tier.id}
            onSelect={() => {
              setSelectedTier(tier);
              // Clear promo code notifications when a tier is selected
              setPromoCodeError('');
              setPromoCodeRedeemed(false);
              setPromoRedeemSuccessMessage('');
              // Clear payment success message when a tier is selected
              setPaymentSuccess(false);
              setSuccessMessage('');
            }}
          />
        ))}
      </TokenTiersContainer>

      {/* Payment Form */}
      {selectedTier && (
        <PaymentForm
          selectedTier={selectedTier}
          promoBonus={isPromoCodeValid ? 1 : 0}
          onPayment={handlePayment}
          isProcessing={isProcessingPayment}
        />
      )}

      {/* Messages */}
      {paymentSuccess && successMessage && (
        <SuccessMessage>
          {successMessage}
        </SuccessMessage>
      )}

      {paymentError && (
        <ErrorMessage>{paymentError}</ErrorMessage>
      )}

      {/* Activity Log */}
      <TokenActivityContainer>
        <h1>TOKEN HISTORY</h1>
        {isLoadingActivities ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <LoadingSpinner />
          </div>
        ) : (
          <ActivityLogList
            activities={tokenActivities}
            title=""
            itemsPerPage={10}
            showPagination={tokenActivities.length > 10}
          />
        )}
      </TokenActivityContainer>
    </TokenPageContainer>
    </Elements >
  );
};